/**
 * @fileoverview Premium Button Component for Skillzy.pro
 *
 * A production-quality button with multiple variants, sizes, loading state,
 * gradient option, and Framer Motion animations.
 *
 * @example
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Get Started
 * </Button>
 *
 * <Button variant="primary" gradient loading>
 *   Saving...
 * </Button>
 *
 * <Button variant="ghost" size="sm" leftIcon={<Plus />}>
 *   Add Item
 * </Button>
 */

import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

/** Spinner SVG for loading state */
const Spinner = ({ className }) => (
  <svg
    className={clsx('animate-spin', className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12" cy="12" r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/** @type {Record<string, string>} */
const variantClasses = {
  primary: clsx(
    'bg-indigo-600 text-white',
    'shadow-lg shadow-indigo-600/20',
    'hover:bg-indigo-700 hover:shadow-indigo-600/30',
    'active:bg-indigo-800',
    'dark:bg-indigo-500 dark:hover:bg-indigo-600',
    'dark:shadow-indigo-500/20'
  ),
  secondary: clsx(
    'bg-white text-slate-700 border border-slate-200',
    'shadow-sm',
    'hover:bg-slate-50 hover:border-slate-300',
    'active:bg-slate-100',
    'dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700',
    'dark:hover:bg-slate-700 dark:hover:border-slate-600'
  ),
  ghost: clsx(
    'bg-transparent text-slate-600',
    'hover:bg-slate-100 hover:text-slate-900',
    'active:bg-slate-200',
    'dark:text-slate-400',
    'dark:hover:bg-slate-800 dark:hover:text-slate-200'
  ),
  danger: clsx(
    'bg-rose-600 text-white',
    'shadow-lg shadow-rose-600/20',
    'hover:bg-rose-700 hover:shadow-rose-600/30',
    'active:bg-rose-800',
    'dark:bg-rose-500 dark:hover:bg-rose-600'
  ),
  success: clsx(
    'bg-emerald-600 text-white',
    'shadow-lg shadow-emerald-600/20',
    'hover:bg-emerald-700 hover:shadow-emerald-600/30',
    'active:bg-emerald-800',
    'dark:bg-emerald-500 dark:hover:bg-emerald-600'
  ),
};

/** Gradient override classes (applied on top of primary) */
const gradientClasses = clsx(
  'bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600',
  'hover:from-indigo-700 hover:via-violet-700 hover:to-purple-700',
  'shadow-lg shadow-indigo-600/25',
  'dark:from-indigo-500 dark:via-violet-500 dark:to-purple-500'
);

/** @type {Record<string, string>} */
const sizeClasses = {
  xs: 'text-xs px-2.5 py-1.5 rounded-md gap-1',
  sm: 'text-sm px-3.5 py-2 rounded-lg gap-1.5',
  md: 'text-sm px-5 py-2.5 rounded-lg gap-2',
  lg: 'text-base px-6 py-3 rounded-xl gap-2.5',
  xl: 'text-base px-8 py-3.5 rounded-xl gap-3',
};

/** @type {Record<string, string>} */
const spinnerSizes = {
  xs: 'h-3 w-3',
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  xl: 'h-5 w-5',
};

/**
 * Premium Button component with variants, sizes, loading state, and animations.
 *
 * @param {Object} props
 * @param {'primary' | 'secondary' | 'ghost' | 'danger' | 'success'} [props.variant='primary'] - Visual style
 * @param {'xs' | 'sm' | 'md' | 'lg' | 'xl'} [props.size='md'] - Button size
 * @param {boolean} [props.gradient=false] - Use gradient background (primary variant only)
 * @param {boolean} [props.loading=false] - Show loading spinner and disable button
 * @param {boolean} [props.disabled=false] - Disable the button
 * @param {boolean} [props.fullWidth=false] - Stretch to full container width
 * @param {React.ReactNode} [props.leftIcon] - Icon element before text
 * @param {React.ReactNode} [props.rightIcon] - Icon element after text
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 * @param {'button' | 'submit' | 'reset'} [props.type='button'] - HTML button type
 */
const Button = forwardRef(({
  variant = 'primary',
  size = 'md',
  gradient = false,
  loading = false,
  disabled = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className,
  children,
  type = 'button',
  ...rest
}, ref) => {
  const isDisabled = disabled || loading;

  const classes = clsx(
    // Base styles
    'inline-flex items-center justify-center',
    'font-semibold',
    'transition-all duration-200 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
    'dark:focus-visible:ring-offset-slate-950',
    'select-none',

    // Size
    sizeClasses[size] || sizeClasses.md,

    // Variant
    gradient && variant === 'primary'
      ? gradientClasses
      : variantClasses[variant] || variantClasses.primary,

    // States
    fullWidth && 'w-full',
    isDisabled && 'opacity-60 cursor-not-allowed pointer-events-none',

    // Custom
    className
  );

  return (
    <motion.button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={classes}
      whileTap={!isDisabled ? { scale: 0.97 } : undefined}
      whileHover={!isDisabled ? { scale: 1.01 } : undefined}
      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
      {...rest}
    >
      {loading ? (
        <Spinner className={spinnerSizes[size] || spinnerSizes.md} />
      ) : leftIcon ? (
        <span className="shrink-0 [&>svg]:h-[1.1em] [&>svg]:w-[1.1em]">{leftIcon}</span>
      ) : null}

      {children && <span className={loading ? 'opacity-70' : ''}>{children}</span>}

      {!loading && rightIcon && (
        <span className="shrink-0 [&>svg]:h-[1.1em] [&>svg]:w-[1.1em]">{rightIcon}</span>
      )}
    </motion.button>
  );
});

Button.displayName = 'Button';

export default Button;
